import { useEffect, useRef, useState } from 'react';
import { ANY_FOLIAGE, FOLIAGE_LABELS, LINE_S, foliageBit, foliageFields, type Body, type FoliageLayer, type LevelDoc, type Vec2 } from '../model/format.ts';
import { invert, apply as applyX, type Xform } from '../model/xform.ts';
import { terrainMissing, terrainSurface, terrainTiles } from '../model/bake.ts';
import { gameData, onTexturesChanged, sortKey } from '../data/gameData.ts';
import { applyCamera, drawScene, drawTile, screenToWorld, viewRect } from '../render/renderer.ts';
import { onTerrainStylesChanged, validateShape, type MissingTile } from '../terrain/index.ts';
import {
  activeLayerOf, draftContainer, draftObject, findObject, getContainer, paletteIndex, useEditor, type EditorState, type Selection,
} from './store.ts';
import { Icon } from './widgets.tsx';
import { bodyBounds, bodyOffset, bodyPath, bodyXform, movingOf, buildScene, layerTiles, objectInfo, objectsInRect, pickAt } from './scene.ts';
import { REMOVE, brushCells, getCell, lineCells, setCells } from './tileOps.ts';
import { badEdges, edgeOk, nearestEdge, pointInTerrain, snapSegment } from './polygon.ts';
import { addHole, contiguousTileCells, createTerrain, detachToBody, ensureLayer, fillObjectCells, findTerrain, moveBody, newWaypoint, placeObjectCell, tilesToPolygonAt } from './actions.ts';
import { allLinks, entityNamer, primarySlot, selectionEntity, targetRefFor, toggleLink, type Link } from './links.ts';
import { ownerRef, type CompAddr } from './components.ts';
import { cameraAddr, cameraBounds, cameraView, writeCameraBounds, type CameraBounds } from './cameraBounds.ts';
import { requestPlay } from '../lobby/client.ts';
import { snapStep, useSettings } from './settings.ts';
import { bodyToLevel, levelShapeAt, shapeToBody } from './bodies.ts';
import { applyFoliage, applyGrassLines, foliageCells, lineAt, lineMark, linePath, linesInRect, sameLine, shiftGrass, surfaceLines, type FoliageMode, type GrassMode, type SurfaceLine } from './grass.ts';
import { applySpikeLines, cornerGuide, destAt, missingSpikeCorners, spikeDests, spikeEditRequest, type SpikeMode } from './spikes.ts';
import { applyResize, frameCorners, handlePoint, handleSides, resizeFrame, resizeTo, type ResizeFrame } from './resize.ts';

type Gesture =
  | { kind: 'none' }
  | { kind: 'pan'; sx: number; sy: number; cam: { x: number; y: number } }
  | { kind: 'pinch'; d0: number; zoom0: number; cx: number; cy: number; cam: { x: number; y: number } }
  | { kind: 'paint'; last: [number, number] | null; erase: boolean }
  | { kind: 'grass'; mode: GrassMode; line: SurfaceLine | null }
  | { kind: 'spikes'; mode: SpikeMode; line: SurfaceLine | null }
  | { kind: 'foliage'; last: [number, number]; mode: FoliageMode }
  | {
      kind: 'rect';
      start: [number, number];
      cur: [number, number];
      purpose: 'fill' | 'erase' | 'detach' | 'select' | 'grass' | 'spikes' | 'foliage' | 'objects';
      grass?: GrassMode;
      spikes?: SpikeMode;
      foliage?: FoliageMode;
    }
  | { kind: 'paintObjects'; last: [number, number] }
  | { kind: 'moveObjects'; start: [number, number]; orig: Map<string, [number, number]>; moved: boolean }
  | { kind: 'moveBody'; id: string; start: [number, number]; applied: [number, number] }
  | { kind: 'moveWaypoint'; body: string; id: string | 'start' }
  | { kind: 'moveVertex'; terrain: string; container: string; ring: number; index: number }
  | { kind: 'moveTerrain'; terrain: string; container: string; start: [number, number]; applied: [number, number] }
  | { kind: 'resize'; id: string; frame: ResizeFrame; side: [number, number]; off: [number, number] }
  /** camera bounds: an edge / corner (side x, y in -1..1) or the whole rect (side 0, 0) */
  | { kind: 'cameraBounds'; addr: CompAddr; side: [number, number]; start: [number, number]; orig: CameraBounds }
  /** touch / right button / spawn pick: undecided until it becomes a tap, a drag, a pinch or a long press */
  | { kind: 'pending'; id: number; down: Down; timer: ReturnType<typeof setTimeout> | undefined };

interface Down {
  clientX: number;
  clientY: number;
  button: number;
  shiftKey: boolean;
  altKey: boolean;
}

const DRAG_PX = 8;
const GRASS_COLOR: Record<GrassMode, string> = { paint: '#7cff78', erase: '#ff6b6b', restore: '#d0d0d0' };
const GRASS_FILL: Record<GrassMode, string> = { paint: 'rgba(124,255,120,0.16)', erase: 'rgba(255,80,80,0.18)', restore: 'rgba(255,255,255,0.12)' };
const SPIKE_COLOR: Record<SpikeMode, string> = { paint: '#ff8a5c', erase: '#ff6b6b' };
const SPIKE_FILL: Record<SpikeMode, string> = { paint: 'rgba(255,138,92,0.16)', erase: 'rgba(255,80,80,0.18)' };

interface ContainerLines {
  surface: number[];
  lines: SurfaceLine[];
  byKey: Map<string, SurfaceLine>;
  /** straight lines (spikes can go on them) */
  sides: SurfaceLine[];
  slopes: SurfaceLine[];
}
const linesCache = new WeakMap<number[], ContainerLines>();
/** The outline lines of a container's terrain surface that grass can be marked on. */
function containerLines(doc: LevelDoc, id: string): ContainerLines {
  const surface = terrainSurface(doc, getContainer(doc, id));
  let c = linesCache.get(surface);
  if (!c) {
    const lines = surfaceLines(surface);
    c = {
      surface,
      lines,
      byKey: new Map(lines.map((l) => [l.x + ',' + l.y + ',' + l.bit, l])),
      sides: lines.filter((l) => l.bit !== LINE_S),
      slopes: lines.filter((l) => l.bit === LINE_S),
    };
    linesCache.set(surface, c);
  }
  return c;
}
const FOLIAGE_COLOR: Record<FoliageMode, string> = { add: '#c8f05a', remove: '#ff6b6b', restore: '#d0d0d0' };
const FOLIAGE_FILL: Record<FoliageMode, string> = { add: 'rgba(200,240,90,0.16)', remove: 'rgba(255,80,80,0.18)', restore: 'rgba(255,255,255,0.12)' };
const LONG_PRESS_MS = 500;
const LINK_RGB: Record<Link['kind'], string> = { activator: '255,170,80', panel: '120,200,255', controlled: '160,255,140' };

/** Paint bits the foliage brush works on. */
const foliageBits = (s: EditorState) => (s.foliage.kind === 'any' ? ANY_FOLIAGE : foliageBit(s.foliage.kind));
/** Decoration layers the foliage brush works on. */
const foliageLayers = (s: EditorState): FoliageLayer[] => (s.foliage.layer === 'both' ? ['front', 'back'] : [s.foliage.layer]);

const snap = (v: number, step: number) => (step > 0 ? Math.round(v / step) * step : v);

const CURSORS = ['ew-resize', 'nesw-resize', 'ns-resize', 'nwse-resize'];
/** CSS resize cursor for a handle side (x/y in -1..1, y up) on a rect rotated by `rotation` degrees. */
function resizeCursor(side: [number, number], rotation: number): string {
  const deg = (Math.atan2(side[1], side[0]) * 180) / Math.PI + rotation;
  return CURSORS[((Math.round(deg / 45) % 4) + 4) % 4];
}

export function Viewport() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<null | { x: number; y: number; world: [number, number]; shape?: string; body?: string; enter: EnterTarget[]; inside?: string }>(null);
  const st = useRef({
    gesture: { kind: 'none' } as Gesture,
    pointers: new Map<number, { x: number; y: number }>(),
    hover: null as [number, number] | null,
    hoverShift: false,
    /** the grass line painted last (Shift-click paints from it to the clicked line) */
    lastLine: null as { container: string; x: number; y: number; bit: number } | null,
    poly: [] as Vec2[],
    space: false,
    /** a stylus is panning because its side button is held */
    penPan: false,
    /** world point of the open context menu */
    menuAt: null as [number, number] | null,
    raf: 0,
    size: [1, 1] as [number, number],
  }).current;

  const requestRender = () => {
    if (!st.raf) st.raf = requestAnimationFrame(render);
  };

  // ---------- coordinate helpers ----------
  const containerXform = (s: EditorState): Xform => {
    const c = getContainer(s.doc!, s.container);
    return c.body ? bodyXform(c.body) : { a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 };
  };
  const toLocal = (s: EditorState, w: [number, number]): [number, number] => applyX(invert(containerXform(s)), w[0], w[1]);
  const toWorld = (s: EditorState, l: [number, number]): [number, number] => applyX(containerXform(s), l[0], l[1]);
  const eventWorld = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = canvasRef.current!.getBoundingClientRect();
    const s = useEditor.getState();
    return screenToWorld(s.camera, r.width, r.height, e.clientX - r.left, e.clientY - r.top);
  };

  // ---------- rendering ----------
  function render() {
    st.raf = 0;
    const canvas = canvasRef.current;
    const s = useEditor.getState();
    if (!canvas || !s.doc) return;
    const doc = s.doc;
    const dpr = window.devicePixelRatio || 1;
    const [w, h] = st.size;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const bg = doc.settings.backgroundColor;
    ctx.fillStyle = `rgb(${bg[0] * 255},${bg[1] * 255},${bg[2] * 255})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = false;
    const cam = { ...s.camera, zoom: s.camera.zoom * dpr };
    applyCamera(ctx, cam, canvas.width, canvas.height);
    const view = viewRect(cam, canvas.width, canvas.height);
    drawScene(ctx, buildScene(doc, { view, scrub: s.scrub, focus: s.container }));
    drawOverlay(ctx, s, doc, view, 1 / cam.zoom);
  }

  function drawOverlay(ctx: CanvasRenderingContext2D, s: EditorState, doc: LevelDoc, view: [number, number, number, number], px: number) {
    ctx.lineJoin = 'round';
    // grid
    if (s.showGrid && 1 / px >= 6) {
      ctx.beginPath();
      for (let x = Math.floor(view[0]); x <= view[2]; x++) { ctx.moveTo(x, view[1]); ctx.lineTo(x, view[3]); }
      for (let y = Math.floor(view[1]); y <= view[3]; y++) { ctx.moveTo(view[0], y); ctx.lineTo(view[2], y); }
      ctx.strokeStyle = 'rgba(255,255,255,0.07)';
      ctx.lineWidth = px;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, view[1]); ctx.lineTo(0, view[3]); ctx.moveTo(view[0], 0); ctx.lineTo(view[2], 0);
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.stroke();
    }
    if (s.container === 'level' && useSettings.getState().showCameraBounds) {
      const g = st.gesture;
      const active = g.kind === 'cameraBounds' ? g.side : st.hover && g.kind === 'none' && s.tool === 'select' ? cameraHandleAt(s, st.hover)?.side ?? null : null;
      drawCameraBounds(ctx, doc, view, px, cameraEditing(s), active);
    }
    const cxf = containerXform(s);
    // focused body outline
    if (s.container !== 'level') {
      const b = doc.bodies.find((x) => x.id === s.container);
      if (b) outlineBody(ctx, doc, b, px, '#ffcc00', s.scrub);
    }
    // bodies: paths and links
    const selBody = s.selection.kind === 'body' ? s.selection.id : s.selection.kind === 'waypoint' ? s.selection.body : null;
    for (const b of doc.bodies) {
      const selected = b.id === selBody;
      if (!selected && !s.showLinks) continue;
      const moving = movingOf(b);
      if (moving && (selected || s.showLinks)) drawPath(ctx, doc, b, px, selected, s);
      if (selected) outlineBody(ctx, doc, b, px, '#4fc3ff', s.scrub);
      if (moving && (selected || s.showLinks)) {
        const bb = bodyBounds(doc, b);
        const off = bodyOffset(b, s.scrub);
        const tx = (bb ? (bb[0] + bb[2]) / 2 : 0) + b.origin[0] + off[0], ty = (bb ? (bb[1] + bb[3]) / 2 : 0) + b.origin[1] + off[1];
        for (const a of moving.activators) {
          const p = entityPos(doc, a);
          if (!p) continue;
          ctx.beginPath();
          ctx.setLineDash([6 * px, 4 * px]);
          ctx.moveTo(p[0], p[1]);
          ctx.lineTo(tx, ty);
          ctx.strokeStyle = selected ? 'rgba(255,120,220,0.95)' : 'rgba(255,120,220,0.35)';
          ctx.lineWidth = (selected ? 2 : 1) * px;
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }
    }
    // activator / panel / controlled-body links of objects and bodies
    {
      const selEnt = selectionEntity(s.selection);
      const selObjs = s.selection.kind === 'objects' ? s.selection.ids.map((id) => `obj:${id}`) : [];
      for (const link of allLinks(doc)) {
        const to = link.to.split('#')[0];
        const sel = link.from === selEnt || to === selEnt || selObjs.includes(link.from) || selObjs.includes(to);
        if (!sel && !s.showLinks) continue;
        const a = entityPos(doc, link.from), b = entityPos(doc, link.to);
        if (!a || !b) continue;
        const rgb = LINK_RGB[link.kind];
        ctx.beginPath();
        ctx.setLineDash(link.kind === 'controlled' ? [2 * px, 3 * px] : [6 * px, 4 * px]);
        ctx.moveTo(b[0], b[1]);
        ctx.lineTo(a[0], a[1]);
        ctx.strokeStyle = `rgba(${rgb},${sel ? 0.95 : 0.3})`;
        ctx.lineWidth = px * (sel ? 2 : 1);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
    // terrains of the focused container
    const cont = getContainer(doc, s.container);
    const showPolys = s.tool === 'polygon' || s.tool === 'foliage' || s.selection.kind === 'terrain';
    ctx.save();
    ctx.transform(cxf.a, cxf.b, cxf.c, cxf.d, cxf.tx, cxf.ty);
    for (const t of cont.terrains) {
      const selected = s.selection.kind === 'terrain' && s.selection.id === t.id;
      if (!showPolys && !selected) continue;
      t.rings.forEach((ring, ri) => {
        ctx.beginPath();
        ring.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
        ctx.closePath();
        ctx.strokeStyle = t.mode === 'subtract' ? 'rgba(255,90,90,0.9)' : selected ? '#7CFFB2' : 'rgba(124,255,178,0.45)';
        ctx.lineWidth = (selected ? 2 : 1) * px;
        if (ri > 0) ctx.setLineDash([4 * px, 3 * px]);
        ctx.stroke();
        ctx.setLineDash([]);
        for (const e of badEdges(t.style, ring)) {
          const a = ring[e], b = ring[(e + 1) % ring.length];
          ctx.beginPath();
          ctx.moveTo(a[0], a[1]);
          ctx.lineTo(b[0], b[1]);
          ctx.strokeStyle = '#ff3b3b';
          ctx.lineWidth = 3 * px;
          ctx.stroke();
        }
        if (selected) {
          for (const p of ring) {
            ctx.fillStyle = '#7CFFB2';
            ctx.fillRect(p[0] - 4 * px, p[1] - 4 * px, 8 * px, 8 * px);
          }
        }
      });
      const eh = selected && st.hover && st.gesture.kind === 'none' && (s.tool === 'select' || (s.tool === 'polygon' && !st.poly.length)) ? edgeAt(s, st.hover) : null;
      if (eh) {
        const p = eh.point;
        ctx.strokeStyle = '#7CFFB2';
        ctx.lineWidth = 1.5 * px;
        ctx.strokeRect(p[0] - 5 * px, p[1] - 5 * px, 10 * px, 10 * px);
        ctx.beginPath();
        ctx.moveTo(p[0] - 3 * px, p[1]);
        ctx.lineTo(p[0] + 3 * px, p[1]);
        ctx.moveTo(p[0], p[1] - 3 * px);
        ctx.lineTo(p[0], p[1] + 3 * px);
        ctx.stroke();
      }
      if (selected) {
        for (const pr of validateShape(t.style, t.rings)) {
          ctx.beginPath();
          ctx.arc(pr.x + 0.5, pr.y + 0.5, 0.35, 0, Math.PI * 2);
          ctx.strokeStyle = '#ff3b3b';
          ctx.lineWidth = 2 * px;
          ctx.stroke();
        }
      }
    }
    // polygon draft
    if (s.tool === 'polygon' && st.poly.length) {
      ctx.beginPath();
      st.poly.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
      ctx.strokeStyle = '#ffe066';
      ctx.lineWidth = 2 * px;
      ctx.stroke();
      if (st.hover) {
        const last = st.poly[st.poly.length - 1];
        const lh = toLocal(s, st.hover);
        const nxt = snapSegment(s.polygon.style, last, lh);
        ctx.beginPath();
        ctx.moveTo(last[0], last[1]);
        ctx.lineTo(nxt[0], nxt[1]);
        ctx.strokeStyle = 'rgba(255,224,102,0.7)';
        ctx.setLineDash([4 * px, 3 * px]);
        ctx.stroke();
        const first = st.poly[0];
        ctx.beginPath();
        ctx.moveTo(nxt[0], nxt[1]);
        ctx.lineTo(first[0], first[1]);
        ctx.strokeStyle = edgeOk(s.polygon.style, nxt, first) ? 'rgba(124,255,178,0.45)' : 'rgba(255,80,80,0.45)';
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#ffe066';
        ctx.fillRect(nxt[0] - 4 * px, nxt[1] - 4 * px, 8 * px, 8 * px);
      }
      for (const p of st.poly) {
        ctx.fillStyle = '#ffe066';
        ctx.fillRect(p[0] - 3 * px, p[1] - 3 * px, 6 * px, 6 * px);
      }
    } else if (s.tool === 'polygon' && st.hover) {
      const lh = toLocal(s, st.hover);
      ctx.fillStyle = '#ffe066';
      ctx.fillRect(Math.round(lh[0]) - 3 * px, Math.round(lh[1]) - 3 * px, 6 * px, 6 * px);
    }
    // brush preview
    if ((s.tool === 'brush' || s.tool === 'erase') && st.hover) {
      const lh = toLocal(s, st.hover);
      const cells = brushCells(Math.floor(lh[0]), Math.floor(lh[1]), s.brush.size);
      for (const [x, y] of cells) {
        if (s.tool === 'brush' && s.brush.entry) {
          ctx.globalAlpha = 0.6;
          drawTile(ctx, [s.brush.entry], x, y, 0, s.brush.flags);
          ctx.globalAlpha = 1;
        }
        ctx.strokeStyle = s.tool === 'erase' ? '#ff6b6b' : '#ffffff';
        ctx.lineWidth = px;
        ctx.strokeRect(x, y, 1, 1);
      }
    }
    if (s.tool === 'grass') {
      const marks = getContainer(doc, s.container).grassLines;
      const { lines, byKey } = containerLines(doc, s.container);
      const seg = (l: SurfaceLine) => {
        ctx.moveTo(l.a[0], l.a[1]);
        ctx.lineTo(l.b[0], l.b[1]);
      };
      ctx.lineCap = 'round';
      // every line that can be marked, with its ends
      ctx.strokeStyle = 'rgba(255,255,255,0.45)';
      ctx.lineWidth = px;
      ctx.beginPath();
      for (const l of lines) seg(l);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      for (const l of lines) for (const p of [l.a, l.b]) ctx.fillRect(p[0] - 1.5 * px, p[1] - 1.5 * px, 3 * px, 3 * px);
      if (marks?.length) {
        for (const [state, color] of [[1, '#7cff78'], [0, '#ff6b6b']] as const) {
          ctx.strokeStyle = color;
          ctx.lineWidth = 3 * px;
          ctx.beginPath();
          for (let i = 0; i + 2 < marks.length; i += 3)
            for (let bit = 1; bit <= LINE_S; bit <<= 1) {
              const l = lineMark(marks[i + 2], bit) === state ? byKey.get(marks[i] + ',' + marks[i + 1] + ',' + bit) : undefined;
              if (l) seg(l);
            }
          ctx.stroke();
        }
      }
      if (st.hover && st.gesture.kind !== 'rect' && !s.grass.rect) {
        const hit = lineAt(lines, toLocal(s, st.hover), lineReach(s));
        if (hit) {
          const from = st.lastLine?.container === s.container ? st.lastLine : null;
          const path = (st.hoverShift || s.grass.join) && from && st.gesture.kind === 'none' ? linePath(lines, from, hit) : null;
          ctx.strokeStyle = GRASS_COLOR[s.grass.mode];
          ctx.globalAlpha = 0.55;
          ctx.lineWidth = 5 * px;
          ctx.beginPath();
          for (const l of path ?? []) seg(l);
          ctx.stroke();
          ctx.globalAlpha = 1;
          ctx.beginPath();
          seg(hit);
          ctx.stroke();
        }
      }
      ctx.lineCap = 'butt';
    }
    if (s.tool === 'spikes') {
      const cont = getContainer(doc, s.container);
      const marks = cont.spikeLines;
      const { lines, byKey, sides, slopes, surface } = containerLines(doc, s.container);
      const missing = missingSpikeCorners(spikeDests(cont, surface));
      const seg = (l: SurfaceLine) => {
        ctx.moveTo(l.a[0], l.a[1]);
        ctx.lineTo(l.b[0], l.b[1]);
      };
      ctx.lineCap = 'round';
      ctx.strokeStyle = 'rgba(255,255,255,0.45)';
      ctx.lineWidth = px;
      ctx.beginPath();
      for (const l of sides) seg(l);
      ctx.stroke();
      ctx.setLineDash([3 * px, 3 * px]);
      ctx.strokeStyle = 'rgba(255,255,255,0.22)';
      ctx.beginPath();
      for (const l of slopes) seg(l);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      for (const l of sides) for (const p of [l.a, l.b]) ctx.fillRect(p[0] - 1.5 * px, p[1] - 1.5 * px, 3 * px, 3 * px);
      if (marks?.length) {
        for (const [state, color] of [[1, '#ff8a5c'], [0, '#ff6b6b']] as const) {
          ctx.strokeStyle = color;
          ctx.lineWidth = 3 * px;
          ctx.beginPath();
          for (let i = 0; i + 2 < marks.length; i += 3)
            for (let bit = 1; bit <= LINE_S; bit <<= 1) {
              if (bit === LINE_S) continue;
              const l = lineMark(marks[i + 2], bit) === state ? byKey.get(marks[i] + ',' + marks[i + 1] + ',' + bit) : undefined;
              if (l) seg(l);
            }
          ctx.stroke();
        }
      }
      for (const m of missing) {
        ctx.fillStyle = 'rgba(255,64,200,0.4)';
        ctx.fillRect(m.x, m.y, 1, 1);
        const g = cornerGuide(m.corner);
        ctx.strokeStyle = '#ff40c8';
        ctx.lineWidth = 4 * px;
        ctx.beginPath();
        for (const [x0, y0, x1, y1] of g.lines) {
          ctx.moveTo(m.x + x0, m.y + y0);
          ctx.lineTo(m.x + x1, m.y + y1);
        }
        ctx.stroke();
      }
      if (st.hover && st.gesture.kind !== 'rect' && !s.spikes.rect) {
        const hit = lineAt(lines, toLocal(s, st.hover), lineReach(s));
        if (hit && hit.bit !== LINE_S) {
          const from = st.lastLine?.container === s.container ? st.lastLine : null;
          const path = (st.hoverShift || s.spikes.join) && from && st.gesture.kind === 'none' ? linePath(lines, from, hit) : null;
          ctx.strokeStyle = SPIKE_COLOR[s.spikes.mode];
          ctx.globalAlpha = 0.55;
          ctx.lineWidth = 5 * px;
          ctx.beginPath();
          for (const l of path ?? []) if (l.bit !== LINE_S) seg(l);
          ctx.stroke();
          ctx.globalAlpha = 1;
          ctx.beginPath();
          seg(hit);
          ctx.stroke();
        }
      }
      ctx.lineCap = 'butt';
    }
    if (s.tool === 'foliage') {
      // the paint layer for the brush's kinds: green = forced in, red = kept out, grey = paint of other kinds
      const bits = foliageBits(s);
      const layers = foliageLayers(s);
      const paint = getContainer(doc, s.container).decoPaint ?? [];
      for (let i = 0; i + 2 < paint.length; i += 3) {
        let add = 0, rem = 0;
        for (const l of layers) {
          const [a, r] = foliageFields(paint[i + 2], l);
          add |= a;
          rem |= r;
        }
        ctx.fillStyle = add & bits ? 'rgba(200,240,90,0.24)' : rem & bits ? 'rgba(255,80,80,0.24)' : 'rgba(255,255,255,0.08)';
        ctx.fillRect(paint[i], paint[i + 1], 1, 1);
      }
      if (st.hover && st.gesture.kind !== 'rect') {
        const lh = toLocal(s, st.hover);
        ctx.strokeStyle = FOLIAGE_COLOR[s.foliage.mode];
        ctx.lineWidth = px;
        for (const [x, y] of brushCells(Math.floor(lh[0]), Math.floor(lh[1]), s.foliage.rect ? 1 : s.foliage.size)) ctx.strokeRect(x, y, 1, 1);
      }
    }
    if (s.tool === 'fix') {
      const fc = s.fixCell;
      if (fc && fc.container === s.container) {
        ctx.fillStyle = 'rgba(255,224,102,0.18)';
        ctx.fillRect(fc.x, fc.y, 1, 1);
        ctx.strokeStyle = '#ffe066';
        ctx.lineWidth = 2 * px;
        ctx.strokeRect(fc.x, fc.y, 1, 1);
      }
      if (st.hover && st.gesture.kind === 'none') {
        const lh = toLocal(s, st.hover);
        ctx.strokeStyle = 'rgba(255,224,102,0.6)';
        ctx.lineWidth = px;
        ctx.strokeRect(Math.floor(lh[0]), Math.floor(lh[1]), 1, 1);
      }
    }
    if ((s.pickTile || s.tool === 'eyedropper' || s.tool === 'rect' || s.tool === 'detach') && st.hover && st.gesture.kind !== 'rect') {
      const lh = toLocal(s, st.hover);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = px;
      ctx.strokeRect(Math.floor(lh[0]), Math.floor(lh[1]), 1, 1);
    }
    if (s.tool === 'tilesToPoly' && st.hover && st.gesture.kind === 'none') {
      const lh = toLocal(s, st.hover);
      const blob = contiguousTileCells(doc, s.container, Math.floor(lh[0]), Math.floor(lh[1]));
      ctx.fillStyle = 'rgba(124,255,178,0.22)';
      ctx.strokeStyle = '#7cffb2';
      ctx.lineWidth = px;
      if (blob.length) for (const [x, y] of blob) ctx.fillRect(x, y, 1, 1);
      else ctx.strokeRect(Math.floor(lh[0]), Math.floor(lh[1]), 1, 1);
    }
    if (s.tool === 'object' && s.objectPrefab && st.hover && st.gesture.kind !== 'rect') {
      const lh = toLocal(s, st.hover);
      ctx.strokeStyle = '#ffe066';
      ctx.lineWidth = px;
      ctx.strokeRect(Math.floor(lh[0]), Math.floor(lh[1]), 1, 1);
    }
    ctx.restore();
    if (useSettings.getState().showMissing || s.tool === 'fix') {
      drawMissing(ctx, terrainMissing(doc, getContainer(doc, s.container)), cxf, px);
      if (s.container === 'level')
        for (const b of doc.bodies) if (!b.hidden && b.terrains.length) drawMissing(ctx, terrainMissing(doc, b), bodyXform(b, bodyOffset(b, s.scrub)), px);
    }
    // rect gestures
    const g = st.gesture;
    if (g.kind === 'rect') {
      const [x0, y0, x1, y1] = rectOf(g, g.purpose !== 'select');
      ctx.save();
      if (g.purpose !== 'select') ctx.transform(cxf.a, cxf.b, cxf.c, cxf.d, cxf.tx, cxf.ty);
      ctx.fillStyle =
        g.purpose === 'grass'
          ? GRASS_FILL[g.grass ?? 'paint']
          : g.purpose === 'spikes'
            ? SPIKE_FILL[g.spikes ?? 'paint']
          : g.purpose === 'foliage'
            ? FOLIAGE_FILL[g.foliage ?? 'add']
            : g.purpose === 'objects'
              ? 'rgba(255,224,102,0.16)'
            : g.purpose === 'detach' ? 'rgba(79,195,255,0.18)' : g.purpose === 'erase' ? 'rgba(255,80,80,0.18)' : 'rgba(255,255,255,0.12)';
      const r = g.purpose === 'select' ? [x0, y0, x1 - x0, y1 - y0] : [x0, y0, x1 - x0 + 1, y1 - y0 + 1];
      ctx.fillRect(r[0], r[1], r[2], r[3]);
      ctx.strokeStyle =
        g.purpose === 'grass' ? GRASS_COLOR[g.grass ?? 'paint'] : g.purpose === 'spikes' ? SPIKE_COLOR[g.spikes ?? 'paint'] : g.purpose === 'foliage' ? FOLIAGE_COLOR[g.foliage ?? 'add'] : g.purpose === 'objects' ? '#ffe066' : g.purpose === 'detach' ? '#4fc3ff' : '#ffffff';
      ctx.lineWidth = px;
      ctx.strokeRect(r[0], r[1], r[2], r[3]);
      ctx.restore();
    }
    if (s.selection.kind === 'cells') {
      const [x0, y0, x1, y1] = s.selection.rect;
      ctx.save();
      ctx.transform(cxf.a, cxf.b, cxf.c, cxf.d, cxf.tx, cxf.ty);
      ctx.setLineDash([4 * px, 3 * px]);
      ctx.strokeStyle = '#4fc3ff';
      ctx.lineWidth = 2 * px;
      ctx.strokeRect(x0, y0, x1 - x0 + 1, y1 - y0 + 1);
      ctx.setLineDash([]);
      ctx.restore();
    }
    // objects: selection, invisible objects, object ghost
    const selIds = new Set(s.selection.kind === 'objects' ? s.selection.ids : []);
    const drawObjBox = (id: string, off: [number, number], color: string, dashed = false) => {
      const f = findObject(doc, id);
      if (!f) return;
      const b = objectInfo(doc, f.obj).bounds;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5 * px;
      if (dashed) ctx.setLineDash([3 * px, 3 * px]);
      ctx.strokeRect(b[0] + off[0], b[1] + off[1], b[2] - b[0], b[3] - b[1]);
      ctx.setLineDash([]);
    };
    const all: [string, [number, number], boolean][] = [
      ...doc.objects.map((o) => [o.id, [0, 0], objectInfo(doc, o).drawables.length === 0] as [string, [number, number], boolean]),
      ...doc.bodies.flatMap((b) => b.objects.map((o) => [o.id, bodyOffset(b, s.scrub), objectInfo(doc, o).drawables.length === 0] as [string, [number, number], boolean])),
    ];
    for (const [id, off, invisible] of all) {
      if (selIds.has(id)) drawObjBox(id, off, '#4fc3ff');
      else if (invisible) drawObjBox(id, off, 'rgba(255,255,255,0.35)', true);
    }
    if (1 / px > 14) {
      ctx.save();
      ctx.scale(1, -1);
      ctx.font = `${11 * px}px system-ui`;
      for (const [id, off, invisible] of all) {
        if (!invisible && !selIds.has(id)) continue;
        const f = findObject(doc, id)!;
        const b = objectInfo(doc, f.obj).bounds;
        ctx.fillStyle = selIds.has(id) ? '#4fc3ff' : 'rgba(255,255,255,0.6)';
        ctx.fillText(f.obj.name, b[0] + off[0], -(b[3] + off[1]) - 3 * px);
      }
      ctx.restore();
    }
    const rh = s.tool === 'select' ? resizable(s) : null;
    if (rh) {
      const cs = frameCorners(rh.frame, rh.off);
      ctx.beginPath();
      cs.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
      ctx.closePath();
      ctx.strokeStyle = '#4fc3ff';
      ctx.lineWidth = 1.5 * px;
      ctx.stroke();
      const active = st.gesture.kind === 'resize' ? st.gesture.side : st.hover ? resizeHandleAt(s, st.hover)?.side : null;
      for (const side of handleSides(rh.frame)) {
        const p = handlePoint(rh.frame, side, rh.off);
        const hot = active && active[0] === side[0] && active[1] === side[1];
        const r = (hot ? 6 : 4.5) * px;
        ctx.fillStyle = hot ? '#4fc3ff' : '#ffffff';
        ctx.fillRect(p[0] - r, p[1] - r, 2 * r, 2 * r);
        ctx.strokeStyle = '#1b6f99';
        ctx.lineWidth = px;
        ctx.strokeRect(p[0] - r, p[1] - r, 2 * r, 2 * r);
      }
    }
    // play-from-here marker: a standing player with its feet at the pointer
    const spawnAt = st.menuAt ?? (s.pickSpawn ? st.hover : null);
    if (spawnAt) {
      ctx.fillStyle = 'rgba(124,255,178,0.25)';
      ctx.fillRect(spawnAt[0] - 0.375, spawnAt[1], 0.75, 1);
      ctx.strokeStyle = '#7cffb2';
      ctx.lineWidth = 2 * px;
      ctx.strokeRect(spawnAt[0] - 0.375, spawnAt[1], 0.75, 1);
    }
  }

  function outlineBody(ctx: CanvasRenderingContext2D, doc: LevelDoc, b: Body, px: number, color: string, scrub: number) {
    const bb = bodyBounds(doc, b);
    if (!bb) return;
    const m = bodyXform(b, bodyOffset(b, scrub));
    ctx.save();
    ctx.transform(m.a, m.b, m.c, m.d, m.tx, m.ty);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2 * px;
    ctx.strokeRect(bb[0], bb[1], bb[2] - bb[0], bb[3] - bb[1]);
    ctx.restore();
  }

  function drawPath(ctx: CanvasRenderingContext2D, doc: LevelDoc, b: Body, px: number, selected: boolean, s: EditorState) {
    const pts = bodyPath(b);
    const bb = bodyBounds(doc, b) ?? [0, 0, 1, 1];
    const cx = (bb[0] + bb[2]) / 2, cy = (bb[1] + bb[3]) / 2;
    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(p[0] + cx, p[1] + cy) : ctx.moveTo(p[0] + cx, p[1] + cy)));
    if (b.moving?.finishAction === 2 && pts.length > 2) ctx.lineTo(pts[0][0] + cx, pts[0][1] + cy);
    ctx.strokeStyle = selected ? 'rgba(79,195,255,0.9)' : 'rgba(79,195,255,0.3)';
    ctx.lineWidth = (selected ? 2 : 1) * px;
    ctx.stroke();
    if (!selected) return;
    // ghosts of the body at each waypoint
    for (const [i, p] of pts.entries()) {
      if (i === 0) continue;
      ctx.save();
      ctx.translate(p[0], p[1]);
      ctx.globalAlpha = 0.3;
      for (const l of b.layers) {
        const t = layerTiles(doc, b, l);
        for (let k = 0; k < t.length; k += 4) if (t[k + 2] >= 0) drawTile(ctx, doc.palette, t[k], t[k + 1], t[k + 2], t[k + 3]);
      }
      ctx.restore();
      ctx.strokeStyle = 'rgba(79,195,255,0.8)';
      ctx.lineWidth = px;
      ctx.setLineDash([3 * px, 3 * px]);
      ctx.strokeRect(p[0] + bb[0], p[1] + bb[1], bb[2] - bb[0], bb[3] - bb[1]);
      ctx.setLineDash([]);
    }
    pts.forEach((p, i) => {
      const wp = i ? b.moving!.waypoints[i - 1] : null;
      const sel = s.selection.kind === 'waypoint' && wp && s.selection.id === wp.id;
      ctx.beginPath();
      ctx.arc(p[0] + cx, p[1] + cy, (i ? 7 : 5) * px, 0, Math.PI * 2);
      ctx.fillStyle = sel ? '#ffe066' : i ? '#4fc3ff' : '#ffffff';
      ctx.fill();
      ctx.save();
      ctx.scale(1, -1);
      ctx.fillStyle = '#000';
      ctx.font = `bold ${9 * px}px system-ui`;
      ctx.textAlign = 'center';
      ctx.fillText(i ? String(i) : 'S', p[0] + cx, -(p[1] + cy) + 3 * px);
      ctx.restore();
    });
  }

  function entityPos(doc: LevelDoc, ref: string): [number, number] | null {
    const [ent] = ref.split('#');
    const [kind, id] = ent.split(':');
    if (kind === 'obj') {
      const f = findObject(doc, id);
      if (!f) return null;
      const b = objectInfo(doc, f.obj).bounds;
      return [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
    }
    if (kind === 'body') {
      const b = doc.bodies.find((x) => x.id === id);
      if (!b) return null;
      const bb = bodyBounds(doc, b) ?? [0, 0, 1, 1];
      return [b.origin[0] + (bb[0] + bb[2]) / 2, b.origin[1] + (bb[1] + bb[3]) / 2];
    }
    return null;
  }

  // ---------- hit tests for handles ----------
  /** the single selected object, when it can be resized with handles */
  function cameraEditing(s: EditorState): boolean {
    const id = s.doc?.system.camera;
    return !!id && s.tool === 'select' && s.container === 'level' && s.selection.kind === 'objects' && s.selection.ids.length === 1 && s.selection.ids[0] === id;
  }

  /** The camera bounds edge, corner or (while editing) move handle under a point. */
  function cameraHandleAt(s: EditorState, w: [number, number]): { side: [number, number]; bounds: CameraBounds } | null {
    if (!s.doc || s.container !== 'level' || !useSettings.getState().showCameraBounds) return null;
    const b = cameraBounds(s.doc);
    if (!b || !b.enabled || b.w <= 0 || b.h <= 0) return null;
    const r = 8 / s.camera.zoom;
    const x1 = b.x + b.w, y1 = b.y + b.h;
    if (cameraEditing(s)) {
      const [cx, cy] = [b.x + b.w / 2, b.y + b.h / 2];
      if (Math.abs(w[0] - cx) < 1.5 * r && Math.abs(w[1] - cy) < 1.5 * r) return { side: [0, 0], bounds: b };
    }
    const inX = w[0] > b.x - r && w[0] < x1 + r, inY = w[1] > b.y - r && w[1] < y1 + r;
    const sx = inY && Math.abs(w[0] - b.x) < r ? -1 : inY && Math.abs(w[0] - x1) < r ? 1 : 0;
    const sy = inX && Math.abs(w[1] - b.y) < r ? -1 : inX && Math.abs(w[1] - y1) < r ? 1 : 0;
    return sx || sy ? { side: [sx, sy], bounds: b } : null;
  }

  function resizable(s: EditorState): { id: string; frame: ResizeFrame; off: [number, number] } | null {
    if (s.selection.kind !== 'objects' || s.selection.ids.length !== 1 || !s.doc) return null;
    const f = findObject(s.doc, s.selection.ids[0]);
    if (!f) return null;
    const frame = resizeFrame(f.obj);
    return frame ? { id: f.obj.id, frame, off: f.body ? bodyOffset(f.body, s.scrub) : [0, 0] } : null;
  }

  function resizeHandleAt(s: EditorState, w: [number, number]) {
    const rh = resizable(s);
    if (!rh) return null;
    const r = 11 / s.camera.zoom;
    let best: { side: [number, number]; d: number } | null = null;
    for (const side of handleSides(rh.frame)) {
      const p = handlePoint(rh.frame, side, rh.off);
      const d = Math.hypot(p[0] - w[0], p[1] - w[1]);
      if (d < r && d < (best?.d ?? Infinity)) best = { side, d };
    }
    return best ? { ...rh, side: best.side } : null;
  }

  function waypointAt(s: EditorState, w: [number, number]): { body: string; id: string | 'start' } | null {
    const doc = s.doc!;
    const selBody = s.selection.kind === 'body' ? s.selection.id : s.selection.kind === 'waypoint' ? s.selection.body : null;
    const b = doc.bodies.find((x) => x.id === selBody);
    if (!b || !movingOf(b)) return null;
    const bb = bodyBounds(doc, b) ?? [0, 0, 1, 1];
    const cx = (bb[0] + bb[2]) / 2, cy = (bb[1] + bb[3]) / 2;
    const r = 10 / s.camera.zoom;
    for (const wp of b.moving!.waypoints) if (Math.hypot(wp.x + cx - w[0], wp.y + cy - w[1]) < r) return { body: b.id, id: wp.id };
    return null;
  }

  function vertexAt(s: EditorState, w: [number, number]): { terrain: string; container: string; ring: number; index: number } | null {
    if (s.selection.kind !== 'terrain') return null;
    const f = findTerrain(s.doc!, s.selection.id);
    if (!f) return null;
    const l = toLocal({ ...s, container: f.container }, w);
    const r = 10 / s.camera.zoom;
    for (let ri = 0; ri < f.terrain.rings.length; ri++) {
      const ring = f.terrain.rings[ri];
      for (let i = 0; i < ring.length; i++) if (Math.hypot(ring[i][0] - l[0], ring[i][1] - l[1]) < r) return { terrain: f.terrain.id, container: f.container, ring: ri, index: i };
    }
    return null;
  }

  /** where a click on an edge of the selected terrain inserts a vertex (index = position in the ring) */
  function edgeAt(s: EditorState, w: [number, number]): { terrain: string; container: string; ring: number; index: number; point: Vec2 } | null {
    if (s.selection.kind !== 'terrain' || vertexAt(s, w)) return null;
    const f = findTerrain(s.doc!, s.selection.id);
    if (!f) return null;
    const l = toLocal({ ...s, container: f.container }, w);
    let best: { terrain: string; container: string; ring: number; index: number; point: Vec2; dist: number } | null = null;
    f.terrain.rings.forEach((ring, ri) => {
      const h = nearestEdge(ring, l[0], l[1], 8 / s.camera.zoom);
      if (h && h.dist < (best?.dist ?? Infinity)) best = { terrain: f.terrain.id, container: f.container, ring: ri, index: h.edge + 1, point: h.point, dist: h.dist };
    });
    return best;
  }

  /** insert a vertex on an edge (unless it lands on an existing corner) and start dragging it */
  function beginEdgeInsert(s: EditorState, hit: NonNullable<ReturnType<typeof edgeAt>>) {
    const ring = findTerrain(s.doc!, hit.terrain)!.terrain.rings[hit.ring];
    const prev = ring[hit.index - 1], next = ring[hit.index % ring.length];
    s.beginLive('Add vertex');
    let index = hit.index;
    if (prev[0] === hit.point[0] && prev[1] === hit.point[1]) index = hit.index - 1;
    else if (next[0] === hit.point[0] && next[1] === hit.point[1]) index = hit.index % ring.length;
    else s.live((d) => {
      const t = draftContainer(d, hit.container).terrains.find((x) => x.id === hit.terrain);
      t?.rings[hit.ring].splice(hit.index, 0, hit.point);
    });
    st.gesture = { kind: 'moveVertex', terrain: hit.terrain, container: hit.container, ring: hit.ring, index };
  }

  function rectOf(g: Extract<Gesture, { kind: 'rect' }>, cells: boolean): [number, number, number, number] {
    if (!cells) return [Math.min(g.start[0], g.cur[0]), Math.min(g.start[1], g.cur[1]), Math.max(g.start[0], g.cur[0]), Math.max(g.start[1], g.cur[1])];
    return [
      Math.floor(Math.min(g.start[0], g.cur[0])), Math.floor(Math.min(g.start[1], g.cur[1])),
      Math.floor(Math.max(g.start[0], g.cur[0])), Math.floor(Math.max(g.start[1], g.cur[1])),
    ];
  }

  // ---------- tile painting ----------
  function paintCells(s: EditorState, cells: [number, number][], erase: boolean) {
    const layer = activeLayerOf(s);
    if (!layer || layer.locked) {
      s.notify(layer?.locked ? 'Layer is locked' : 'No layer selected');
      return;
    }
    const doc = s.doc!;
    const cont = getContainer(doc, s.container);
    const gen = erase ? terrainTiles(doc, cont).get(layer.id) ?? [] : [];
    s.live((d) => {
      const dc = draftContainer(d, s.container);
      const l = dc.layers.find((x) => x.id === layer.id);
      if (!l) return;
      if (erase) {
        l.tiles = setCells(l.tiles as number[], cells.map(([x, y]) => ({ x, y, p: getCell(gen, x, y) ? -1 : REMOVE, f: 0 })));
      } else if (s.brush.entry) {
        const p = paletteIndex(d, s.brush.entry);
        l.tiles = setCells(l.tiles as number[], cells.map(([x, y]) => ({ x, y, p, f: s.brush.flags })));
      }
    });
  }

  // ---------- grass paint ----------
  const GRASS_LABEL: Record<GrassMode, string> = { paint: 'Paint grass', erase: 'Erase grass', restore: 'Restore auto grass' };

  /** how far from a surface line a click still picks it (tiles) */
  function lineReach(s: EditorState) {
    return Math.max(0.5, 14 / s.camera.zoom);
  }

  function paintLines(s: EditorState, lines: SurfaceLine[], mode: GrassMode) {
    if (!lines.length) return;
    s.live((d) => {
      const dc = draftContainer(d, s.container);
      const next = applyGrassLines(dc.grassLines, lines, mode);
      if (next === dc.grassLines) return;
      if (next) dc.grassLines = next;
      else delete dc.grassLines;
    });
    const l = lines[lines.length - 1];
    st.lastLine = { container: s.container, x: l.x, y: l.y, bit: l.bit };
  }

  const SPIKE_LABEL: Record<SpikeMode, string> = { paint: 'Paint spikes', erase: 'Erase spikes' };

  function paintSpikeLines(s: EditorState, lines: SurfaceLine[], mode: SpikeMode) {
    const sides = lines.filter((l) => l.bit !== LINE_S);
    if (!sides.length) return;
    s.live((d) => {
      const dc = draftContainer(d, s.container);
      ensureLayer(dc, 'spikes', s.container !== 'level');
      const next = applySpikeLines(dc.spikeLines, sides, mode);
      if (next === dc.spikeLines) return;
      if (next) dc.spikeLines = next;
      else delete dc.spikeLines;
    });
    const l = sides[sides.length - 1];
    st.lastLine = { container: s.container, x: l.x, y: l.y, bit: l.bit };
  }

  function clickSpikeLine(s: EditorState, local: [number, number], mode: SpikeMode, between: boolean): SurfaceLine | null {
    const { lines } = containerLines(s.doc!, s.container);
    const hit = lineAt(lines, local, lineReach(s));
    if (!hit) return null;
    if (hit.bit === LINE_S) {
      s.notify('Spikes cannot go on angled slopes');
      return null;
    }
    const from = st.lastLine?.container === s.container ? st.lastLine : null;
    let seq = [hit];
    if (between && from && !sameLine(from, hit)) {
      const path = linePath(lines, from, hit);
      if (path) seq = path.filter((l) => l.bit !== LINE_S);
      else s.notify('That line is not on the same outline as the last one: marked just this line');
    }
    paintSpikeLines(s, seq, mode);
    return hit;
  }

  function openSpikeCorner(s: EditorState, local: [number, number]) {
    const cont = getContainer(s.doc!, s.container);
    const dests = spikeDests(cont, terrainSurface(s.doc!, cont));
    const hit = destAt(dests, local);
    if (!hit?.corner) return;
    const queue = dests
      .filter((d) => d.corner && (d.x !== hit.x || d.y !== hit.y))
      .map((d) => ({ container: s.container, x: d.x, y: d.y, layer: 'spike' as const, corner: d.corner! }));
    const req = spikeEditRequest(s.container, hit, queue.length ? queue : undefined);
    if (req) s.set({ tileEdit: req });
  }

  /** Lines mode click: the line under the pointer, or with Shift (or Fill between) every line from the last one to it. */
  function clickLine(s: EditorState, local: [number, number], mode: GrassMode, between: boolean): SurfaceLine | null {
    const { lines } = containerLines(s.doc!, s.container);
    const hit = lineAt(lines, local, lineReach(s));
    if (!hit) return null;
    const from = st.lastLine?.container === s.container ? st.lastLine : null;
    let seq = [hit];
    if (between && from && !sameLine(from, hit)) {
      const path = linePath(lines, from, hit);
      if (path) seq = path;
      else s.notify('That line is not on the same outline as the last one: marked just this line');
    }
    paintLines(s, seq, mode);
    return hit;
  }

  // ---------- foliage paint ----------
  function foliageLabel(s: EditorState, mode: FoliageMode): string {
    const kind = s.foliage.kind === 'any' ? 'foliage' : FOLIAGE_LABELS[s.foliage.kind].toLowerCase();
    const what = s.foliage.layer === 'both' ? kind : `${s.foliage.layer === 'front' ? 'foreground' : 'background'} ${kind}`;
    return mode === 'add' ? `Add ${what}` : mode === 'remove' ? `Remove ${what}` : `Restore auto ${what}`;
  }

  function paintFoliage(s: EditorState, cells: [number, number][], mode: FoliageMode) {
    const cont = getContainer(s.doc!, s.container);
    const target = mode === 'restore' ? cells : foliageCells(cont.terrains, cells);
    if (!target.length) return;
    const bits = foliageBits(s);
    s.live((d) => {
      const dc = draftContainer(d, s.container);
      const next = applyFoliage(dc.decoPaint, target, mode, bits, foliageLayers(s));
      if (next === dc.decoPaint) return;
      if (next) dc.decoPaint = next;
      else delete dc.decoPaint;
    });
  }

  function eyedrop(s: EditorState, local: [number, number]) {
    const doc = s.doc!;
    const cont = getContainer(doc, s.container);
    const x = Math.floor(local[0]), y = Math.floor(local[1]);
    const active = activeLayerOf(s);
    const order = [...cont.layers].sort((a, b) => (a.id === active?.id ? -1 : b.id === active?.id ? 1 : 0));
    for (const l of order) {
      const c = getCell(layerTiles(doc, cont, l), x, y);
      if (c && c[0] >= 0) {
        s.set({ brush: { ...s.brush, entry: doc.palette[c[0]], flags: c[1] }, tool: 'brush', activeLayer: { ...s.activeLayer, [s.container]: l.id } });
        s.notify(`Picked ${doc.palette[c[0]].sprite} from ${l.name}`);
        return;
      }
    }
    s.notify('No tile here');
  }

  /** answers the tile editor's "pick from level": the top visible tile under the pointer */
  function pickTileAt(s: EditorState, local: [number, number]) {
    const doc = s.doc!;
    const cont = getContainer(doc, s.container);
    const x = Math.floor(local[0]), y = Math.floor(local[1]);
    const layers = cont.layers.filter((l) => l.visible !== false).sort((a, b) => sortKey(b.sortingLayer, b.sortingOrder) - sortKey(a.sortingLayer, a.sortingOrder));
    for (const l of layers) {
      const c = getCell(layerTiles(doc, cont, l), x, y);
      if (c && c[0] >= 0) return s.pickTile?.(doc.palette[c[0]], c[1]);
    }
    s.notify('No tile here');
  }

  // ---------- polygon ----------
  function polygonClick(s: EditorState, w: [number, number]) {
    const l = toLocal(s, w);
    if (!st.poly.length) {
      st.poly = [[Math.round(l[0]), Math.round(l[1])]];
      return;
    }
    const last = st.poly[st.poly.length - 1];
    const p = snapSegment(s.polygon.style, last, l);
    const first = st.poly[0];
    if (p[0] === first[0] && p[1] === first[1]) {
      finishPolygon(s);
      return;
    }
    if (p[0] === last[0] && p[1] === last[1]) return;
    st.poly = [...st.poly, p];
  }

  function finishPolygon(s: EditorState) {
    const ring = st.poly;
    if (ring.length < 3) {
      s.notify('A shape needs at least 3 points');
      return;
    }
    if (!edgeOk(s.polygon.style, ring[ring.length - 1], ring[0])) {
      s.notify('Closing edge is not a supported slope; add another point');
      return;
    }
    st.poly = [];
    if (s.polygon.hole && s.selection.kind === 'terrain') {
      const f = findTerrain(s.doc!, s.selection.id);
      if (f) {
        addHole(f.container, f.terrain.id, ring);
        return;
      }
    }
    const id = createTerrain(s.container, ring, s.polygon.style, s.polygon.mode);
    s.set({ selection: { kind: 'terrain', id } });
  }

  // ---------- pointer handling ----------
  const PEN_RESUME = new Set<string>(['brush', 'erase', 'grass', 'spikes', 'foliage']);
  /** S Pen and other styluses: side (barrel) button, or the eraser button some browsers report instead */
  const penButton = (e: React.PointerEvent) => e.pointerType === 'pen' && (e.buttons & (2 | 32)) !== 0;

  function startPan(e: { clientX: number; clientY: number }) {
    const s = useEditor.getState();
    st.gesture = { kind: 'pan', sx: e.clientX, sy: e.clientY, cam: { x: s.camera.x, y: s.camera.y } };
  }

  /** End the running tool gesture the way lifting the pointer would, then pan with the stylus. */
  function switchToPenPan(e: React.PointerEvent) {
    const s = useEditor.getState();
    const g = st.gesture;
    if (g.kind === 'pending') clearTimeout(g.timer);
    else if (g.kind === 'moveVertex' || g.kind === 'paint' || g.kind === 'paintObjects' || g.kind === 'grass' || g.kind === 'spikes' || g.kind === 'foliage' || g.kind === 'moveBody' || g.kind === 'moveTerrain' || g.kind === 'moveWaypoint' || g.kind === 'resize' || g.kind === 'cameraBounds') s.commitLive();
    else if (g.kind === 'moveObjects' && g.moved) s.commitLive();
    st.penPan = true;
    startPan(e);
  }

  function onPointerDown(e: React.PointerEvent) {
    const canvas = canvasRef.current!;
    canvas.setPointerCapture(e.pointerId);
    st.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const s = useEditor.getState();
    if (!s.doc) return;
    if (st.menuAt) {
      closeMenu();
      return;
    }
    if (st.pointers.size === 2) {
      // second finger: switch to pinch, cancel tool gesture
      if (st.gesture.kind === 'pending') clearTimeout(st.gesture.timer);
      else if (st.gesture.kind === 'paint' || st.gesture.kind === 'paintObjects' || st.gesture.kind === 'grass' || st.gesture.kind === 'foliage' || st.gesture.kind.startsWith('move')) s.commitLive();
      const [a, b] = [...st.pointers.values()];
      st.gesture = { kind: 'pinch', d0: Math.hypot(a.x - b.x, a.y - b.y), zoom0: s.camera.zoom, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2, cam: { x: s.camera.x, y: s.camera.y } };
      return;
    }
    if (st.pointers.size > 2) return;
    if (penButton(e)) {
      st.penPan = true;
      startPan(e);
      return;
    }
    const down: Down = { clientX: e.clientX, clientY: e.clientY, button: e.button, shiftKey: e.shiftKey, altKey: e.altKey };
    const touch = e.pointerType === 'touch';
    // a first finger may be the start of a pinch, so touch input waits until the gesture is clear
    if (e.button === 2 || (e.button === 0 && (touch || s.pickSpawn || s.pickTile) && !st.space)) {
      const pending: Gesture = { kind: 'pending', id: e.pointerId, down, timer: undefined };
      if (touch) pending.timer = setTimeout(() => { if (st.gesture === pending) openMenu(down); }, LONG_PRESS_MS);
      st.gesture = pending;
      return;
    }
    beginTool(down);
  }

  /** a drag that starts here pans the view instead of running the tool */
  function dragPans(s: EditorState, w: [number, number]): boolean {
    if (s.pickSpawn || s.pickTile) return true;
    switch (s.tool) {
      case 'pan':
      case 'object':
        return false;
      case 'eyedropper':
      case 'link':
      case 'tilesToPoly':
        return true;
      case 'polygon':
        return !(s.selection.kind === 'terrain' && (vertexAt(s, w) || (!st.poly.length && edgeAt(s, w))));
      case 'waypoint':
        return !waypointAt(s, w);
      default:
        return false;
    }
  }

  function openMenu(d: { clientX: number; clientY: number }) {
    st.gesture = { kind: 'none' };
    const r = wrapRef.current!.getBoundingClientRect();
    const w = eventWorld(d);
    st.menuAt = w;
    const s = useEditor.getState();
    const shape = s.doc && s.container === 'level' ? levelShapeAt(s.doc, w[0], w[1])?.id : undefined;
    const pick = s.doc ? pickAt(s.doc, w[0], w[1], s.scrub, true) : null;
    const pb = pick?.kind === 'body' ? s.doc!.bodies.find((b) => b.id === pick.id) : undefined;
    const body = pb && !pb.owner && (pb.terrains.length || pb.layers.some((l) => l.tiles.length)) ? pb.id : undefined;
    const enter = s.doc ? enterTargets(s.doc, w, s.scrub, pb, s.container) : [];
    const inside = s.doc && s.container !== 'level' ? s.doc.bodies.find((b) => b.id === s.container)?.name ?? 'platform' : undefined;
    const rows = 2 + (shape ? 1 : 0) + (body ? 1 : 0) + enter.length + (inside ? 1 : 0);
    setMenu({ x: Math.min(d.clientX - r.left, r.width - 260), y: Math.min(d.clientY - r.top, r.height - 12 - rows * 44), world: w, shape, body, enter, inside });
    navigator.vibrate?.(10);
    requestRender();
  }

  function closeMenu() {
    if (!st.menuAt) return;
    st.menuAt = null;
    setMenu(null);
    requestRender();
  }

  function beginTool(e: Down) {
    const s = useEditor.getState();
    if (!s.doc) return;
    const w = eventWorld(e);
    if (e.button === 1 || e.button === 2 || st.space || s.tool === 'pan') {
      st.gesture = { kind: 'pan', sx: e.clientX, sy: e.clientY, cam: { x: s.camera.x, y: s.camera.y } };
      return;
    }
    const local = toLocal(s, w);
    switch (s.tool) {
      case 'select': {
        const ch = cameraHandleAt(s, w);
        const addr = ch && cameraAddr(s.doc);
        if (ch && addr) {
          s.set({ selection: { kind: 'objects', ids: [s.doc.system.camera!] } });
          s.beginLive('Camera bounds');
          st.gesture = { kind: 'cameraBounds', addr, side: ch.side, start: w, orig: ch.bounds };
          return;
        }
        const rh = resizeHandleAt(s, w);
        if (rh) {
          s.beginLive('Resize');
          st.gesture = { kind: 'resize', id: rh.id, frame: rh.frame, side: rh.side, off: rh.off };
          return;
        }
        const wp = waypointAt(s, w);
        if (wp) {
          s.beginLive('Move waypoint');
          st.gesture = { kind: 'moveWaypoint', body: wp.body, id: wp.id };
          if (wp.id !== 'start') s.set({ selection: { kind: 'waypoint', body: wp.body, id: wp.id } });
          return;
        }
        const vx = vertexAt(s, w);
        if (vx) {
          s.beginLive('Move vertex');
          st.gesture = { kind: 'moveVertex', ...vx };
          return;
        }
        const eh = edgeAt(s, w);
        if (eh) {
          beginEdgeInsert(s, eh);
          requestRender();
          return;
        }
        const pick = pickAt(s.doc, w[0], w[1], s.scrub, s.container !== 'level');
        if (pick?.kind === 'object') {
          let ids = s.selection.kind === 'objects' ? s.selection.ids : [];
          if (e.shiftKey) ids = ids.includes(pick.id) ? ids.filter((i) => i !== pick.id) : [...ids, pick.id];
          else if (!ids.includes(pick.id)) ids = [pick.id];
          s.set({ selection: { kind: 'objects', ids }, panel: s.panelOpen ? 'inspector' : s.panel });
          const orig = new Map<string, [number, number]>();
          for (const id of ids) {
            const f = findObject(s.doc, id);
            if (f) orig.set(id, [f.obj.x, f.obj.y]);
          }
          s.beginLive('Move objects');
          st.gesture = { kind: 'moveObjects', start: w, orig, moved: false };
          return;
        }
        // terrain in focused container
        const cont = getContainer(s.doc, s.container);
        const t = [...cont.terrains].reverse().find((tt) => pointInTerrain(tt, local[0], local[1]));
        if (pick?.kind === 'body' && (!t || s.container === 'level')) {
          s.set({ selection: { kind: 'body', id: pick.id }, panel: s.panelOpen ? 'inspector' : s.panel });
          s.beginLive('Move body');
          st.gesture = { kind: 'moveBody', id: pick.id, start: w, applied: [0, 0] };
          return;
        }
        if (t) {
          s.set({ selection: { kind: 'terrain', id: t.id }, panel: s.panelOpen ? 'inspector' : s.panel });
          s.beginLive('Move terrain');
          st.gesture = { kind: 'moveTerrain', terrain: t.id, container: s.container, start: w, applied: [0, 0] };
          return;
        }
        st.gesture = { kind: 'rect', start: w, cur: w, purpose: 'select' };
        return;
      }
      case 'polygon': {
        if (s.selection.kind === 'terrain') {
          const vx = vertexAt(s, w);
          if (vx) {
            s.beginLive('Move vertex');
            st.gesture = { kind: 'moveVertex', ...vx };
            return;
          }
          const eh = st.poly.length ? null : edgeAt(s, w);
          if (eh) {
            beginEdgeInsert(s, eh);
            requestRender();
            return;
          }
        }
        polygonClick(s, w);
        requestRender();
        return;
      }
      case 'brush':
      case 'erase': {
        const erase = s.tool === 'erase' || e.altKey;
        s.beginLive(erase ? 'Erase tiles' : 'Paint tiles');
        const cell: [number, number] = [Math.floor(local[0]), Math.floor(local[1])];
        paintCells(s, brushCells(cell[0], cell[1], s.brush.size), erase);
        st.gesture = { kind: 'paint', last: cell, erase };
        return;
      }
      case 'rect':
        st.gesture = { kind: 'rect', start: local, cur: local, purpose: e.altKey ? 'erase' : 'fill' };
        return;
      case 'grass': {
        const mode: GrassMode = e.altKey ? 'erase' : s.grass.mode;
        if (s.grass.rect) {
          st.gesture = { kind: 'rect', start: local, cur: local, purpose: 'grass', grass: mode };
          return;
        }
        s.beginLive(GRASS_LABEL[mode]);
        st.gesture = { kind: 'grass', mode, line: clickLine(s, local, mode, e.shiftKey || s.grass.join) };
        return;
      }
      case 'spikes': {
        const mode: SpikeMode = e.altKey ? 'erase' : s.spikes.mode;
        if (s.spikes.rect) {
          st.gesture = { kind: 'rect', start: local, cur: local, purpose: 'spikes', spikes: mode };
          return;
        }
        s.beginLive(SPIKE_LABEL[mode]);
        st.gesture = { kind: 'spikes', mode, line: clickSpikeLine(s, local, mode, e.shiftKey || s.spikes.join) };
        return;
      }
      case 'foliage': {
        const mode: FoliageMode = e.altKey ? 'remove' : s.foliage.mode;
        if (s.foliage.rect) {
          st.gesture = { kind: 'rect', start: local, cur: local, purpose: 'foliage', foliage: mode };
          return;
        }
        s.beginLive(foliageLabel(s, mode));
        const cell: [number, number] = [Math.floor(local[0]), Math.floor(local[1])];
        paintFoliage(s, brushCells(cell[0], cell[1], s.foliage.size), mode);
        st.gesture = { kind: 'foliage', last: cell, mode };
        return;
      }
      case 'detach':
        if (s.container !== 'level') {
          s.notify('Detach works on the level; exit the body first');
          return;
        }
        st.gesture = { kind: 'rect', start: local, cur: local, purpose: 'detach' };
        return;
      case 'tilesToPoly':
        tilesToPolygonAt(local[0], local[1]);
        requestRender();
        return;
      case 'eyedropper':
        eyedrop(s, local);
        return;
      case 'fix':
        s.set({ fixCell: { container: s.container, x: Math.floor(local[0]), y: Math.floor(local[1]) } });
        requestRender();
        return;
      case 'object': {
        if (!s.objectPrefab) {
          s.notify('Pick an object in the Objects panel');
          return;
        }
        if (s.objectPlace === 'rect') {
          st.gesture = { kind: 'rect', start: local, cur: local, purpose: 'objects' };
          return;
        }
        const def = gameData().prefabs[s.objectPrefab];
        const name = def?.name ?? 'Object';
        const bodyId = s.container !== 'level' ? s.container : undefined;
        const cell: [number, number] = [Math.floor(local[0]), Math.floor(local[1])];
        s.beginLive(`Place ${name}`);
        const id = placeObjectCell(cell[0], cell[1], s.objectPrefab, name, bodyId);
        if (id) s.set({ selection: { kind: 'objects', ids: [id] } });
        st.gesture = { kind: 'paintObjects', last: cell };
        return;
      }
      case 'waypoint': {
        const wp = waypointAt(s, w);
        if (wp) {
          s.beginLive('Move waypoint');
          st.gesture = { kind: 'moveWaypoint', body: wp.body, id: wp.id };
          return;
        }
        const bodyId = s.selection.kind === 'body' ? s.selection.id : s.selection.kind === 'waypoint' ? s.selection.body : null;
        const b = s.doc.bodies.find((x) => x.id === bodyId);
        if (!b) {
          const pick = pickAt(s.doc, w[0], w[1], s.scrub, true);
          if (pick?.kind === 'body') s.set({ selection: { kind: 'body', id: pick.id } });
          else s.notify('Select a body first');
          return;
        }
        if (!movingOf(b) || !b.moving) {
          s.notify('This block is not a moving platform (change its type in the inspector)');
          return;
        }
        const bb = bodyBounds(s.doc, b) ?? [0, 0, 1, 1];
        const x = Math.round(w[0] - (bb[0] + bb[2]) / 2), y = Math.round(w[1] - (bb[1] + bb[3]) / 2);
        const nw = newWaypoint(x, y, b.moving.waypoints.length);
        s.change('Add waypoint', (d) => {
          d.bodies.find((q) => q.id === b.id)!.moving!.waypoints.push(nw);
        });
        s.set({ selection: { kind: 'waypoint', body: b.id, id: nw.id } });
        return;
      }
      case 'link': {
        const pick = pickAt(s.doc, w[0], w[1], s.scrub);
        linkTap(s, pick ? (pick.kind === 'body' ? `body:${pick.id}` : `obj:${pick.id}`) : null);
        return;
      }
    }
  }

  /**
   * Link tool tap. With a slot picked in the inspector (or the selection's main activator list), the tapped
   * entity is linked into it; when the selection is itself an activator, it is linked into the tapped target.
   */
  function linkTap(s: EditorState, ent: string | null) {
    const doc = s.doc!;
    const name = entityNamer(doc);
    const selEnt = selectionEntity(s.selection);
    const slot = s.linkSlot ?? (selEnt ? primarySlot(doc, selEnt) : null);
    if (!ent) {
      if (!slot) s.notify('Tap a platform, laser, gate… then tap what should drive it');
      return;
    }
    if (slot && ent !== ownerRef(slot.addr)) {
      const ref = targetRefFor(doc, ent, slot.accept);
      if (ref) {
        const on = toggleLink(slot, ref);
        s.notify(`${on ? 'Linked' : 'Unlinked'} ${name(ref)}`);
        if (s.linkSlot && !slot.list && on) s.set({ tool: 'select' });
        return;
      }
      if (s.linkSlot) {
        s.notify(slot.accept === 'panel' ? 'That is not a control panel' : slot.accept === 'body' ? 'Tap a moving platform or block' : 'That is not a switch or activator');
        return;
      }
    }
    if (selEnt && ent !== selEnt) {
      const actRef = targetRefFor(doc, selEnt, 'activator');
      const target = primarySlot(doc, ent);
      if (actRef && target) {
        const on = toggleLink(target, actRef);
        s.notify(`${name(ent)} ${on ? 'now uses' : 'no longer uses'} ${name(actRef)}`);
        return;
      }
    }
    const [kind, id] = ent.split(':');
    s.set({ selection: kind === 'body' ? { kind: 'body', id } : { kind: 'objects', ids: [id] } });
    s.notify(primarySlot(doc, ent) ? `Now tap the switches that drive ${name(ent)}` : `${name(ent)} selected. Tap something it should drive`);
  }

  function onPointerMove(e: React.PointerEvent) {
    const s = useEditor.getState();
    if (!s.doc) return;
    if (st.pointers.has(e.pointerId)) st.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const w = eventWorld(e);
    st.hover = w;
    st.hoverShift = e.shiftKey;
    if (st.pointers.has(e.pointerId) && st.pointers.size === 1 && e.pointerType === 'pen') {
      if (!st.penPan && penButton(e) && st.gesture.kind !== 'pan') switchToPenPan(e);
      else if (st.penPan && !penButton(e) && PEN_RESUME.has(s.tool)) {
        // side button let go with the pen still down: painting tools carry on from here
        st.penPan = false;
        st.gesture = { kind: 'none' };
        beginTool({ clientX: e.clientX, clientY: e.clientY, button: 0, shiftKey: e.shiftKey, altKey: e.altKey });
      }
    }
    const p = st.gesture;
    if (p.kind === 'pending') {
      if (e.pointerId !== p.id) return;
      if (Math.hypot(e.clientX - p.down.clientX, e.clientY - p.down.clientY) < DRAG_PX) {
        requestRender();
        return;
      }
      clearTimeout(p.timer);
      if (p.down.button === 2 || dragPans(s, eventWorld(p.down))) st.gesture = { kind: 'pan', sx: p.down.clientX, sy: p.down.clientY, cam: { x: s.camera.x, y: s.camera.y } };
      else beginTool(p.down);
    }
    const g = st.gesture;
    switch (g.kind) {
      case 'pan': {
        const z = s.camera.zoom;
        s.set({ camera: { ...s.camera, x: g.cam.x - (e.clientX - g.sx) / z, y: g.cam.y + (e.clientY - g.sy) / z } });
        break;
      }
      case 'pinch': {
        if (st.pointers.size < 2) break;
        const [a, b] = [...st.pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        const zoom = clampZoom(g.zoom0 * (d / (g.d0 || 1)));
        const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
        const r = canvasRef.current!.getBoundingClientRect();
        // keep the world point under the initial pinch centre under the current centre
        const wx = g.cam.x + (g.cx - r.left - r.width / 2) / g.zoom0;
        const wy = g.cam.y - (g.cy - r.top - r.height / 2) / g.zoom0;
        s.set({ camera: { zoom, x: wx - (cx - r.left - r.width / 2) / zoom, y: wy + (cy - r.top - r.height / 2) / zoom } });
        break;
      }
      case 'paint': {
        const l = toLocal(s, w);
        const cell: [number, number] = [Math.floor(l[0]), Math.floor(l[1])];
        if (g.last && cell[0] === g.last[0] && cell[1] === g.last[1]) break;
        const line = g.last ? lineCells(g.last[0], g.last[1], cell[0], cell[1]) : [cell];
        const cells = line.flatMap(([x, y]) => brushCells(x, y, s.brush.size));
        paintCells(s, cells, g.erase);
        g.last = cell;
        break;
      }
      case 'grass': {
        // dragging along a surface marks the lines passed, also those the pointer skipped
        const { lines } = containerLines(s.doc, s.container);
        const hit = lineAt(lines, toLocal(s, w), lineReach(s));
        if (!hit || sameLine(hit, g.line)) break;
        const path = g.line ? linePath(lines, g.line, hit) : null;
        paintLines(s, path && path.length <= 8 ? path.slice(1) : [hit], g.mode);
        g.line = hit;
        break;
      }
      case 'spikes': {
        const { lines } = containerLines(s.doc, s.container);
        const hit = lineAt(lines, toLocal(s, w), lineReach(s));
        if (!hit || hit.bit === LINE_S || sameLine(hit, g.line)) break;
        const path = g.line ? linePath(lines, g.line, hit) : null;
        paintSpikeLines(s, path && path.length <= 8 ? path.slice(1) : [hit], g.mode);
        g.line = hit;
        break;
      }
      case 'foliage': {
        const l = toLocal(s, w);
        const cell: [number, number] = [Math.floor(l[0]), Math.floor(l[1])];
        if (cell[0] === g.last[0] && cell[1] === g.last[1]) break;
        paintFoliage(s, lineCells(g.last[0], g.last[1], cell[0], cell[1]).flatMap(([x, y]) => brushCells(x, y, s.foliage.size)), g.mode);
        g.last = cell;
        break;
      }
      case 'paintObjects': {
        if (!s.objectPrefab) break;
        const l = toLocal(s, w);
        const cell: [number, number] = [Math.floor(l[0]), Math.floor(l[1])];
        if (cell[0] === g.last[0] && cell[1] === g.last[1]) break;
        const def = gameData().prefabs[s.objectPrefab];
        const name = def?.name ?? 'Object';
        const bodyId = s.container !== 'level' ? s.container : undefined;
        const ids: string[] = [];
        for (const [x, y] of lineCells(g.last[0], g.last[1], cell[0], cell[1])) {
          const id = placeObjectCell(x, y, s.objectPrefab, name, bodyId);
          if (id) ids.push(id);
        }
        if (ids.length) s.set({ selection: { kind: 'objects', ids } });
        g.last = cell;
        break;
      }
      case 'rect':
        g.cur = g.purpose === 'select' ? w : toLocal(s, w);
        break;
      case 'moveObjects': {
        let dx = w[0] - g.start[0], dy = w[1] - g.start[1];
        if (!g.moved && Math.hypot(dx, dy) * s.camera.zoom < 4) break;
        g.moved = true;
        if (!e.shiftKey) { dx = snap(dx, snapStep()); dy = snap(dy, snapStep()); }
        s.live((d) => {
          for (const [id, [ox, oy]] of g.orig) {
            const o = draftObject(d, id);
            if (o) { o.x = ox + dx; o.y = oy + dy; }
          }
        });
        break;
      }
      case 'moveBody': {
        const dx = Math.round(w[0] - g.start[0]), dy = Math.round(w[1] - g.start[1]);
        if (dx === g.applied[0] && dy === g.applied[1]) break;
        const ddx = dx - g.applied[0], ddy = dy - g.applied[1];
        g.applied = [dx, dy];
        s.live((d) => moveBody(d, g.id, ddx, ddy, !e.altKey));
        break;
      }
      case 'moveTerrain': {
        const dx = Math.round(w[0] - g.start[0]), dy = Math.round(w[1] - g.start[1]);
        if (dx === g.applied[0] && dy === g.applied[1]) break;
        const ddx = dx - g.applied[0], ddy = dy - g.applied[1];
        g.applied = [dx, dy];
        s.live((d) => {
          const dc = draftContainer(d, g.container);
          const t = dc.terrains.find((x) => x.id === g.terrain);
          if (!t) return;
          if (dc.grassPaint) dc.grassPaint = shiftGrass(dc.grassPaint, t, ddx, ddy);
          if (dc.grassLines) dc.grassLines = shiftGrass(dc.grassLines, t, ddx, ddy);
          if (dc.spikeLines) dc.spikeLines = shiftGrass(dc.spikeLines, t, ddx, ddy);
          if (dc.decoPaint) dc.decoPaint = shiftGrass(dc.decoPaint, t, ddx, ddy);
          t.rings = t.rings.map((r) => r.map(([x, y]) => [x + ddx, y + ddy] as Vec2));
        });
        break;
      }
      case 'moveVertex': {
        const l = toLocal({ ...s, container: g.container }, w);
        const p: Vec2 = [Math.round(l[0]), Math.round(l[1])];
        s.live((d) => {
          const t = draftContainer(d, g.container).terrains.find((x) => x.id === g.terrain);
          if (t) t.rings[g.ring][g.index] = p;
        });
        break;
      }
      case 'resize': {
        const res = resizeTo(g.frame, g.side, [w[0] - g.off[0], w[1] - g.off[1]], e.shiftKey ? 0 : snapStep());
        s.live((d) => applyResize(d, g.id, g.frame, res));
        break;
      }
      case 'cameraBounds': {
        const step = e.shiftKey ? 0 : Math.max(snapStep(), 0.5);
        const o = g.orig;
        let x0 = o.x, y0 = o.y, x1 = o.x + o.w, y1 = o.y + o.h;
        if (g.side[0] === 0 && g.side[1] === 0) {
          const dx = snap(w[0] - g.start[0], step), dy = snap(w[1] - g.start[1], step);
          x0 += dx; x1 += dx; y0 += dy; y1 += dy;
        } else {
          if (g.side[0] < 0) x0 = Math.min(snap(w[0], step), x1 - 1);
          if (g.side[0] > 0) x1 = Math.max(snap(w[0], step), x0 + 1);
          if (g.side[1] < 0) y0 = Math.min(snap(w[1], step), y1 - 1);
          if (g.side[1] > 0) y1 = Math.max(snap(w[1], step), y0 + 1);
        }
        s.live((d) => writeCameraBounds(d, g.addr, { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }));
        break;
      }
      case 'none': {
        const canvas = canvasRef.current;
        if (canvas && s.tool === 'select') {
          const cs = cameraHandleAt(s, w)?.side;
          const side = cs ? null : resizeHandleAt(s, w)?.side;
          canvas.style.cursor = cs ? (cs[0] || cs[1] ? resizeCursor(cs, 0) : 'move') : side ? resizeCursor(side, resizable(s)!.frame.rotation) : '';
        }
        break;
      }
      case 'moveWaypoint': {
        const b = s.doc.bodies.find((x) => x.id === g.body);
        if (!b) break;
        const bb = bodyBounds(s.doc, b) ?? [0, 0, 1, 1];
        const x = e.shiftKey ? w[0] - (bb[0] + bb[2]) / 2 : Math.round(w[0] - (bb[0] + bb[2]) / 2);
        const y = e.shiftKey ? w[1] - (bb[1] + bb[3]) / 2 : Math.round(w[1] - (bb[1] + bb[3]) / 2);
        s.live((d) => {
          const wp = d.bodies.find((q) => q.id === g.body)?.moving?.waypoints.find((q) => q.id === g.id);
          if (wp) { wp.x = x; wp.y = y; }
        });
        break;
      }
    }
    requestRender();
  }

  function onPointerUp(e: React.PointerEvent) {
    st.pointers.delete(e.pointerId);
    st.penPan = false;
    const s = useEditor.getState();
    const p = st.gesture;
    if (p.kind === 'pending') {
      if (e.pointerId !== p.id) return;
      clearTimeout(p.timer);
      st.gesture = { kind: 'none' };
      if (e.type === 'pointercancel' || !s.doc) return;
      if (p.down.button === 2) return openMenu(p.down);
      if (s.pickSpawn) return requestPlay(eventWorld(p.down));
      if (s.pickTile) return pickTileAt(s, toLocal(s, eventWorld(p.down)));
      beginTool(p.down);
    }
    const g = st.gesture;
    if (g.kind === 'pinch') {
      if (st.pointers.size === 0) st.gesture = { kind: 'none' };
      return;
    }
    st.gesture = { kind: 'none' };
    if (!s.doc) return;
    switch (g.kind) {
      case 'moveVertex':
        // dropping a vertex onto a neighbour merges the two
        s.live((d) => {
          const ring = draftContainer(d, g.container).terrains.find((x) => x.id === g.terrain)?.rings[g.ring];
          if (!ring || ring.length <= 3) return;
          const p = ring[g.index];
          const same = (q: Vec2) => q[0] === p[0] && q[1] === p[1];
          if (same(ring[(g.index + ring.length - 1) % ring.length]) || same(ring[(g.index + 1) % ring.length])) ring.splice(g.index, 1);
        });
        s.commitLive();
        break;
      case 'paint':
      case 'paintObjects':
      case 'grass':
      case 'spikes':
      case 'foliage':
      case 'moveBody':
      case 'moveTerrain':
      case 'moveWaypoint':
      case 'resize':
      case 'cameraBounds':
        s.commitLive();
        break;
      case 'moveObjects':
        if (g.moved) s.commitLive();
        break;
      case 'rect': {
        if (g.purpose === 'select') {
          const r = rectOf(g, false);
          if ((r[2] - r[0]) * s.camera.zoom < 4 && (r[3] - r[1]) * s.camera.zoom < 4) {
            s.set({ selection: { kind: 'none' } });
          } else {
            const ids = objectsInRect(s.doc, r);
            s.set({ selection: ids.length ? { kind: 'objects', ids } : { kind: 'none' } });
          }
          break;
        }
        const [x0, y0, x1, y1] = rectOf(g, true);
        if (g.purpose === 'detach') {
          s.set({ selection: { kind: 'cells', rect: [x0, y0, x1, y1] } });
          detachToBody([x0, y0, x1, y1]);
          break;
        }
        const cells: [number, number][] = [];
        for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) cells.push([x, y]);
        if (cells.length > 40000) {
          s.notify('Area too large');
          break;
        }
        if (g.purpose === 'grass') {
          const mode = g.grass ?? 'paint';
          s.beginLive(GRASS_LABEL[mode]);
          paintLines(s, linesInRect(containerLines(s.doc, s.container).lines, x0, y0, x1, y1), mode);
          s.commitLive();
          break;
        }
        if (g.purpose === 'spikes') {
          const mode = g.spikes ?? 'paint';
          s.beginLive(SPIKE_LABEL[mode]);
          paintSpikeLines(s, linesInRect(containerLines(s.doc, s.container).lines, x0, y0, x1, y1), mode);
          s.commitLive();
          break;
        }
        if (g.purpose === 'foliage') {
          const mode = g.foliage ?? 'add';
          s.beginLive(foliageLabel(s, mode));
          paintFoliage(s, cells, mode);
          s.commitLive();
          break;
        }
        if (g.purpose === 'objects') {
          if (!s.objectPrefab) {
            s.notify('Pick an object in the Objects panel');
            break;
          }
          const def = gameData().prefabs[s.objectPrefab];
          const name = def?.name ?? 'Object';
          const n = fillObjectCells(cells, s.objectPrefab, name, s.container !== 'level' ? s.container : undefined);
          if (n) s.notify(`${n} ${name}${n === 1 ? '' : 's'}`);
          break;
        }
        s.beginLive(g.purpose === 'erase' ? 'Erase area' : 'Fill area');
        paintCells(s, cells, g.purpose === 'erase');
        s.commitLive();
        break;
      }
    }
    requestRender();
  }

  function clampZoom(z: number) {
    return Math.max(2, Math.min(160, z));
  }

  function onWheel(e: WheelEvent) {
    e.preventDefault();
    const s = useEditor.getState();
    const r = canvasRef.current!.getBoundingClientRect();
    if (!e.ctrlKey && Math.abs(e.deltaX) > Math.abs(e.deltaY) * 0.5 && e.deltaMode === 0 && !Number.isInteger(e.deltaY)) {
      // trackpad two-finger scroll pans
      s.set({ camera: { ...s.camera, x: s.camera.x + e.deltaX / s.camera.zoom, y: s.camera.y - e.deltaY / s.camera.zoom } });
      return;
    }
    const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015));
    const zoom = clampZoom(s.camera.zoom * factor);
    const [wx, wy] = screenToWorld(s.camera, r.width, r.height, e.clientX - r.left, e.clientY - r.top);
    const sx = e.clientX - r.left - r.width / 2, sy = e.clientY - r.top - r.height / 2;
    s.set({ camera: { zoom, x: wx - sx / zoom, y: wy + sy / zoom } });
  }

  // ---------- keyboard ----------
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest?.('input, textarea, select')) return;
      const s = useEditor.getState();
      if (s.playing) return;
      if (s.pickTile && e.key === 'Escape') {
        s.set({ pickTile: null });
        return;
      }
      if (s.tileEdit && !s.pickTile) return;
      if (e.code === 'Space') {
        st.space = true;
        return;
      }
      if (e.key === 'Escape' && (st.menuAt || s.pickSpawn)) {
        closeMenu();
        s.set({ pickSpawn: false });
        return;
      }
      if (e.key === 'Escape' && s.linkSlot) {
        s.set({ tool: 'select' });
        return;
      }
      if (s.tool === 'polygon') {
        if (e.key === 'Enter') { finishPolygon(s); requestRender(); return; }
        if (e.key === 'Escape' && st.poly.length) { st.poly = []; requestRender(); return; }
        if (e.key === 'Backspace' && st.poly.length) { st.poly = st.poly.slice(0, -1); requestRender(); e.preventDefault(); return; }
      }
      handleShortcut(e, s);
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') st.space = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  // ---------- lifecycle ----------
  useEffect(() => {
    const canvas = canvasRef.current!;
    const wrap = wrapRef.current!;
    const ro = new ResizeObserver(() => {
      const r = wrap.getBoundingClientRect();
      st.size = [r.width, r.height];
      requestRender();
    });
    ro.observe(wrap);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    const unsub = useEditor.subscribe(requestRender);
    const unsubStyles = onTerrainStylesChanged(requestRender);
    const unsubTextures = onTexturesChanged(requestRender);
    const unsubSettings = useSettings.subscribe(requestRender);
    const onPolyFinish = () => { finishPolygon(useEditor.getState()); requestRender(); };
    const onPolyCancel = () => { st.poly = []; requestRender(); };
    window.addEventListener('ple:polygon-finish', onPolyFinish);
    window.addEventListener('ple:polygon-cancel', onPolyCancel);
    requestRender();
    return () => {
      ro.disconnect();
      canvas.removeEventListener('wheel', onWheel);
      unsub();
      unsubStyles();
      unsubTextures();
      unsubSettings();
      window.removeEventListener('ple:polygon-finish', onPolyFinish);
      window.removeEventListener('ple:polygon-cancel', onPolyCancel);
      cancelAnimationFrame(st.raf);
      st.raf = 0;
    };
  }, []);

  return (
    <div ref={wrapRef} className="viewport">
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => { st.hover = null; requestRender(); }}
        onDoubleClick={(e) => {
          const s = useEditor.getState();
          if (s.tool === 'polygon') { finishPolygon(s); requestRender(); return; }
          if (s.tool === 'spikes' && s.doc) {
            const r = canvasRef.current!.getBoundingClientRect();
            const w = screenToWorld(s.camera, r.width, r.height, e.clientX - r.left, e.clientY - r.top);
            openSpikeCorner(s, toLocal(s, w));
          }
        }}
        onContextMenu={(e) => e.preventDefault()}
      />
      {menu && (
        <div className="menu ctx-menu" style={{ left: Math.max(4, menu.x), top: Math.max(4, menu.y) }} onContextMenu={(e) => e.preventDefault()}>
          {menu.enter.map((t) => (
            <button key={t.id} className="ctx-enter" title={t.label} onClick={() => { closeMenu(); useEditor.getState().set({ container: t.id, selection: { kind: 'body', id: t.id } }); }}>
              <Icon name="enter" size={16} /> <span className="ctx-label">{t.label}</span>
            </button>
          ))}
          {menu.inside && (
            <button onClick={() => { closeMenu(); useEditor.getState().set({ container: 'level' }); }}>
              <Icon name="exit" size={16} /> <span className="ctx-label">Done editing {menu.inside}</span>
            </button>
          )}
          <button onClick={() => { closeMenu(); requestPlay(menu.world); }}><Icon name="play" size={16} /> Play from here</button>
          <button onClick={() => { closeMenu(); const s = useEditor.getState(); s.set({ camera: { ...s.camera, x: menu.world[0], y: menu.world[1] } }); }}><Icon name="target" size={16} /> Centre view here</button>
          {menu.shape && !menu.body && (
            <button onClick={() => { closeMenu(); shapeToBody(menu.shape!); }}><Icon name="detach" size={16} /> Make moving platform</button>
          )}
          {menu.body && (
            <button onClick={() => { closeMenu(); bodyToLevel(menu.body!); }}><Icon name="exit" size={16} /> Move back to level</button>
          )}
        </div>
      )}
    </div>
  );
}

/** Pink cells: terrain on a slope its tileset has no tile for. A fill when the dirt is missing, an outline for grass only. */
function drawMissing(ctx: CanvasRenderingContext2D, cells: MissingTile[], m: Xform, px: number) {
  if (!cells.length) return;
  ctx.save();
  ctx.transform(m.a, m.b, m.c, m.d, m.tx, m.ty);
  ctx.fillStyle = 'rgba(255,64,200,0.28)';
  for (const c of cells) if (c.dirt) ctx.fillRect(c.x, c.y, 1, 1);
  ctx.beginPath();
  const k = Math.min(0.1, 2 * px);
  for (const c of cells) ctx.rect(c.x + k, c.y + k, 1 - 2 * k, 1 - 2 * k);
  ctx.setLineDash([4 * px, 3 * px]);
  ctx.strokeStyle = '#ff4fd8';
  ctx.lineWidth = 1.5 * px;
  ctx.stroke();
  ctx.restore();
}

const CAMERA_COLOR = '#ffb347';

/** Camera bounds: the area outside shaded, the outline and label; while editing, handles and the camera's view size. */
function drawCameraBounds(ctx: CanvasRenderingContext2D, doc: LevelDoc, view: [number, number, number, number], px: number, edit: boolean, active: [number, number] | null) {
  const b = cameraBounds(doc);
  if (!b || !b.enabled || b.w <= 0 || b.h <= 0) return;
  const x1 = b.x + b.w, y1 = b.y + b.h;
  ctx.save();
  ctx.beginPath();
  ctx.rect(view[0] - 1, view[1] - 1, view[2] - view[0] + 2, view[3] - view[1] + 2);
  ctx.rect(b.x, b.y, b.w, b.h);
  ctx.fillStyle = edit ? 'rgba(0,0,0,0.38)' : 'rgba(0,0,0,0.22)';
  ctx.fill('evenodd');
  ctx.strokeStyle = CAMERA_COLOR;
  ctx.lineWidth = (edit ? 2.5 : 1.5) * px;
  if (!edit) ctx.setLineDash([8 * px, 5 * px]);
  ctx.strokeRect(b.x, b.y, b.w, b.h);
  ctx.setLineDash([]);
  if (active && (active[0] || active[1])) {
    ctx.lineWidth = 4 * px;
    ctx.beginPath();
    if (active[0]) { const x = active[0] < 0 ? b.x : x1; ctx.moveTo(x, b.y); ctx.lineTo(x, y1); }
    if (active[1]) { const y = active[1] < 0 ? b.y : y1; ctx.moveTo(b.x, y); ctx.lineTo(x1, y); }
    ctx.stroke();
  }
  if (edit) {
    const [vw, vh] = cameraView(doc);
    ctx.setLineDash([4 * px, 4 * px]);
    ctx.strokeStyle = 'rgba(255,179,71,0.7)';
    ctx.lineWidth = px;
    ctx.strokeRect(b.x, y1 - Math.min(vh, b.h), Math.min(vw, b.w), Math.min(vh, b.h));
    ctx.setLineDash([]);
    const handles: [number, number][] = [[-1, -1], [0, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [0, 1], [1, 1]];
    for (const [sx, sy] of handles) {
      const hx = b.x + ((sx + 1) / 2) * b.w, hy = b.y + ((sy + 1) / 2) * b.h;
      const hot = active && active[0] === sx && active[1] === sy;
      const r = (hot ? 6.5 : 5) * px;
      ctx.fillStyle = hot ? CAMERA_COLOR : '#ffffff';
      ctx.fillRect(hx - r, hy - r, 2 * r, 2 * r);
      ctx.strokeStyle = '#8a5200';
      ctx.lineWidth = px;
      ctx.strokeRect(hx - r, hy - r, 2 * r, 2 * r);
    }
    // move handle
    const cx = b.x + b.w / 2, cy = b.y + b.h / 2, r = 12 * px;
    const hot = active && !active[0] && !active[1];
    ctx.fillStyle = hot ? CAMERA_COLOR : 'rgba(255,179,71,0.85)';
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#3a2400';
    ctx.lineWidth = 1.5 * px;
    ctx.beginPath();
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + dx * r * 0.7, cy + dy * r * 0.7);
    }
    ctx.stroke();
  }
  // label above the top left corner
  if (b.w / px > 120) {
    ctx.scale(1, -1);
    ctx.font = `600 ${12 * px}px system-ui`;
    ctx.fillStyle = CAMERA_COLOR;
    const label = `Camera bounds ${Math.round(b.w * 10) / 10} × ${Math.round(b.h * 10) / 10}`;
    ctx.fillText(edit ? label + '  (drag edges, corners or the centre; Shift: no snap)' : label, b.x + 4 * px, -y1 - 6 * px);
    if (edit) {
      const [vw, vh] = cameraView(doc);
      ctx.font = `${11 * px}px system-ui`;
      ctx.fillStyle = 'rgba(255,179,71,0.85)';
      ctx.fillText(`camera view ${Math.round(vw)} × ${Math.round(vh)} (16:9)`, b.x + 4 * px, -y1 + 14 * px);
    }
  }
  ctx.restore();
}

interface EnterTarget { id: string; label: string }

/** Bodies the context menu can step into at a point: those owned by the object there, the body under it, and the one it rides. */
function enterTargets(doc: LevelDoc, w: [number, number], scrub: number, under: Body | undefined, container: string): EnterTarget[] {
  const out: EnterTarget[] = [];
  const add = (b: Body | undefined, label: string) => {
    if (b && b.id !== container && !out.some((t) => t.id === b.id) && out.length < 4) out.push({ id: b.id, label });
  };
  const pick = pickAt(doc, w[0], w[1], scrub);
  const found = pick?.kind === 'object' ? findObject(doc, pick.id) : null;
  if (found) {
    const owned = doc.bodies.filter((b) => b.owner?.object === found.obj.id);
    for (const b of owned) add(b, `Edit inside ${owned.length > 1 ? b.name : found.obj.name}`);
  }
  if (under) {
    const owner = under.owner && findObject(doc, under.owner.object)?.obj;
    add(under, `Edit inside ${owner ? owner.name : under.name}`);
  }
  if (found?.body) add(found.body, `Edit inside ${found.body.name}`);
  return out;
}

const TOOL_KEYS: Record<string, EditorState['tool']> = {
  v: 'select', h: 'pan', p: 'polygon', u: 'tilesToPoly', b: 'brush', e: 'erase', r: 'rect', i: 'eyedropper', o: 'object', w: 'waypoint', l: 'link', d: 'detach', t: 'grass', k: 'spikes', n: 'foliage', c: 'fix',
};

export function handleShortcut(e: KeyboardEvent, s: EditorState) {
  const mod = e.ctrlKey || e.metaKey;
  const k = e.key.toLowerCase();
  if (mod && k === 'z') { e.preventDefault(); e.shiftKey ? s.redo() : s.undo(); return; }
  if (mod && k === 'y') { e.preventDefault(); s.redo(); return; }
  if (mod && k === 's') { e.preventDefault(); window.dispatchEvent(new Event('ple:save')); return; }
  if (mod && k === 'd') { e.preventDefault(); window.dispatchEvent(new Event('ple:duplicate')); return; }
  if (mod) return;
  if (k === 'tab' && !s.modal) { e.preventDefault(); s.set({ panelOpen: !s.panelOpen }); return; }
  if (k === 'delete' || k === 'backspace') { window.dispatchEvent(new Event('ple:delete')); return; }
  if (k === 'escape') { s.set({ selection: { kind: 'none' } }); return; }
  if (k === 'g') { s.set({ showGrid: !s.showGrid }); return; }
  if (k === 'f') { window.dispatchEvent(new Event('ple:focus')); return; }
  if (k === 'x' && s.tool === 'brush') { window.dispatchEvent(new CustomEvent('ple:brush', { detail: 'flipx' })); return; }
  if (k === 'y' && s.tool === 'brush') { window.dispatchEvent(new CustomEvent('ple:brush', { detail: 'flipy' })); return; }
  if (k === 'z' && s.tool === 'brush') { window.dispatchEvent(new CustomEvent('ple:brush', { detail: 'rotate' })); return; }
  if (k === 'arrowleft' || k === 'arrowright' || k === 'arrowup' || k === 'arrowdown') {
    window.dispatchEvent(new CustomEvent('ple:nudge', { detail: { key: k, big: e.shiftKey } }));
    e.preventDefault();
    return;
  }
  const t = TOOL_KEYS[k];
  if (t) s.set({ tool: t });
}

export type { Selection };
